import type { PropostaDoBrain } from '@/data/brain';

/**
 * A ação que espera decisão na tela de voz (B9, decisão do usuário em 13/09/2026).
 *
 * Nasce de uma `PropostaDoBrain`: quem guarda, trava e executa continua sendo o servidor
 * (`decidir` na borda — um clique só leva a proposta, e ela vence em 30 minutos). Aqui fica o
 * que o cartão mostra. `preview` já aceita prévias mais ricas (mensagem, campanha, documento)
 * sem mexer na tela de voz; hoje só a mensagem ganha prévia própria.
 */

export type RiscoDaAcao = 'normal' | 'sensitive' | 'destructive';

export type TipoDePrevia = 'message' | 'campaign' | 'appointment' | 'payment' | 'email' | 'document' | 'generic';

export interface PendingAction {
    id: string;
    type: string;

    title: string;
    description?: string;

    confirmLabel?: string;
    cancelLabel?: string;

    risk?: RiscoDaAcao;

    /** Dados resumidos exibidos no cartão. */
    details?: Array<{ label: string; value: string }>;

    /** Prévia opcional da ação futura. */
    preview?: {
        type: TipoDePrevia;
        title?: string;
        summary?: string;
        content?: unknown;
    };

    payload: unknown;
}

interface ModeloDaAcao {
    titulo: string;
    risco: RiscoDaAcao;
    previa?: TipoDePrevia;
    confirmar?: string;
    feito: string;
}

/* Financeira, destrutiva ou comunicação externa: `sensitive`/`destructive` — o cartão diz. */
const MODELOS: Record<string, ModeloDaAcao> = {
    propor_agendamento: { titulo: 'Agendar paciente', risco: 'normal', previa: 'appointment', feito: 'Agendamento confirmado.' },
    propor_remarcar: { titulo: 'Remarcar agendamento', risco: 'normal', previa: 'appointment', feito: 'Agendamento remarcado.' },
    propor_cancelar_agendamento: { titulo: 'Cancelar agendamento', risco: 'destructive', previa: 'appointment', confirmar: 'Cancelar agendamento', feito: 'Agendamento cancelado.' },
    propor_tarefa: { titulo: 'Criar tarefa', risco: 'normal', feito: 'Tarefa criada.' },
    propor_reconectar_canal: { titulo: 'Reconectar canal', risco: 'normal', feito: 'Pronto: o QR code ou os campos do e-mail estão na conversa.' },
    propor_lead: { titulo: 'Criar lead', risco: 'normal', feito: 'Lead criado.' },
    propor_mover_lead: { titulo: 'Mover lead no funil', risco: 'normal', feito: 'Lead movido.' },
    propor_pagamento: { titulo: 'Registrar pagamento', risco: 'sensitive', previa: 'payment', feito: 'Pagamento registrado.' },
    propor_procedimento: { titulo: 'Salvar procedimento', risco: 'normal', feito: 'Procedimento salvo.' },
    propor_produto: { titulo: 'Salvar produto', risco: 'normal', feito: 'Produto salvo.' },
    propor_categoria_produto: { titulo: 'Criar categoria de produto', risco: 'normal', feito: 'Categoria criada.' },
    propor_movimentacao_estoque: { titulo: 'Movimentar estoque', risco: 'normal', feito: 'Estoque atualizado.' },
    propor_contagem_estoque: { titulo: 'Ajustar pela contagem', risco: 'normal', feito: 'Saldo ajustado.' },
    propor_titulo: { titulo: 'Criar lançamento', risco: 'sensitive', previa: 'payment', feito: 'Lançamento criado.' },
    propor_editar_titulo: { titulo: 'Editar lançamento', risco: 'sensitive', previa: 'payment', feito: 'Lançamento editado.' },
    propor_transferencia: { titulo: 'Transferir entre contas', risco: 'sensitive', previa: 'payment', confirmar: 'Transferir', feito: 'Transferência registrada.' },
    propor_conta_financeira: { titulo: 'Salvar conta financeira', risco: 'sensitive', feito: 'Conta salva.' },
    propor_categoria_financeira: { titulo: 'Criar categoria de conta', risco: 'normal', feito: 'Categoria criada.' },
    propor_forma_de_pagamento: { titulo: 'Criar forma de pagamento', risco: 'normal', feito: 'Forma de pagamento criada.' },
    propor_situacao_tarefa: { titulo: 'Atualizar tarefa', risco: 'normal', feito: 'Tarefa atualizada.' },
    propor_situacao_agendamento: { titulo: 'Atualizar agendamento', risco: 'normal', previa: 'appointment', feito: 'Agendamento atualizado.' },
    propor_paciente: { titulo: 'Cadastrar paciente', risco: 'normal', feito: 'Paciente cadastrado.' },
    propor_mensagem: { titulo: 'Enviar mensagem', risco: 'sensitive', previa: 'message', confirmar: 'Enviar', feito: 'Mensagem enviada.' },
};

const CAMPO_DA_MENSAGEM = /^(mensagem|texto|conte[uú]do)$/i;

export function paraAcaoPendente(proposta: PropostaDoBrain): PendingAction {
    const modelo = MODELOS[proposta.ferramenta];
    const details = proposta.campos.map((c) => ({ label: c.rotulo, value: c.valor }));
    const acao: PendingAction = {
        id: proposta.id,
        type: proposta.ferramenta,
        title: modelo?.titulo ?? (proposta.resumo || 'Ação proposta'),
        ...(modelo && proposta.resumo ? { description: proposta.resumo } : {}),
        confirmLabel: modelo?.confirmar ?? 'Confirmar',
        cancelLabel: 'Agora não',
        risk: modelo?.risco ?? 'normal',
        details,
        payload: proposta,
    };
    if (modelo?.previa === 'message') {
        /* O texto da mensagem vai inteiro na prévia, e não repetido nos dados. */
        const texto = proposta.campos.find((c) => CAMPO_DA_MENSAGEM.test(c.rotulo.trim()))?.valor;
        acao.preview = texto ? { type: 'message', content: texto } : { type: 'message' };
        if (texto) acao.details = details.filter((d) => !CAMPO_DA_MENSAGEM.test(d.label.trim()));
    } else if (modelo?.previa) {
        acao.preview = { type: modelo.previa };
    }
    return acao;
}

export interface PropostaNaConversa {
    mensagemId: string;
    proposta: PropostaDoBrain;
}

/** As propostas que ainda esperam decisão, na ordem em que chegaram. `decididas` já saíram da fila. */
export function propostasPendentes(
    mensagens: ReadonlyArray<{ id: string; propostas: readonly PropostaDoBrain[] }>,
    decididas: ReadonlySet<string>,
): PropostaNaConversa[] {
    return mensagens.flatMap((m) => m.propostas
        .filter((p) => p.situacao === 'pendente' && !decididas.has(p.id))
        .map((proposta) => ({ mensagemId: m.id, proposta })));
}

function primeiraFrase(texto: string | undefined): string {
    const limpo = (texto ?? '').replace(/\s+/g, ' ').trim();
    const achada = /^.{1,160}?[.!?](?=\s|$)/.exec(limpo);
    return (achada ? achada[0] : limpo.slice(0, 160)).trim();
}

/** O que foi feito, curto, para o cartão de desfecho ("Agendamento confirmado."). */
export function textoDoFeito(ferramenta: string): string {
    return MODELOS[ferramenta]?.feito ?? 'A ação foi executada.';
}

/** O que a VecchIA fala depois da decisão ("Pronto. Agendamento confirmado."). Sem identificador nem valor longo. */
export function falaDoDesfecho(proposta: PropostaDoBrain): string {
    switch (proposta.situacao) {
        case 'confirmada':
            return `Pronto. ${MODELOS[proposta.ferramenta]?.feito ?? 'Feito.'}`;
        case 'cancelada':
            return 'Tudo bem, não fiz nada.';
        case 'falhou':
            return `Não consegui fazer. ${primeiraFrase(proposta.motivo) || 'A ação falhou no servidor.'}`;
        case 'expirada':
            return 'Essa proposta expirou, então não fiz nada. Se ainda quiser, é só pedir de novo.';
        case 'pendente':
            return '';
    }
}
